/*
 * Product recommendations are injected asynchronously by Shopify. The theme's
 * generic slider assumes LTR `offsetLeft` values, which can calculate zero
 * pages in a right-to-left recommendations carousel. This component owns only
 * the related-products controls and derives the visible-card count from the
 * rendered geometry instead.
 */
(() => {
  class ZoriaRelatedProductsSlider extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.slider = this.querySelector('[id^="Slider-"]');
      this.previousButton = this.querySelector('button[name="previous"]');
      this.nextButton = this.querySelector('button[name="next"]');
      this.currentElement = this.querySelector('.slider-counter--current');
      this.totalElement = this.querySelector('.slider-counter--total');
      if (!this.slider || !this.previousButton || !this.nextButton) return;

      this.onScroll = this.onScroll.bind(this);
      this.onPrevious = this.onPrevious.bind(this);
      this.onNext = this.onNext.bind(this);
      this.slider.addEventListener('scroll', this.onScroll, { passive: true });
      this.previousButton.addEventListener('click', this.onPrevious, true);
      this.nextButton.addEventListener('click', this.onNext, true);
      this.resizeObserver = new ResizeObserver(() => this.queueRefresh());
      this.resizeObserver.observe(this.slider);
      this.queueRefresh();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.slider?.removeEventListener('scroll', this.onScroll);
      this.previousButton?.removeEventListener('click', this.onPrevious, true);
      this.nextButton?.removeEventListener('click', this.onNext, true);
    }

    get slides() {
      return Array.from(this.slider.querySelectorAll('[id^="Slide-"]')).filter((slide) => slide.offsetWidth > 0);
    }

    get isRtl() {
      return getComputedStyle(this.slider).direction === 'rtl';
    }

    get progress() {
      // Chromium uses negative scrollLeft for RTL overflow. Keep the result in
      // the forward (next-product) direction and clamp browser differences.
      const maximum = Math.max(0, this.slider.scrollWidth - this.slider.clientWidth);
      const raw = this.slider.scrollLeft;
      const value = this.isRtl ? (raw <= 0 ? -raw : maximum - raw) : raw;
      return Math.min(maximum, Math.max(0, value));
    }

    queueRefresh() {
      cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = requestAnimationFrame(() => this.refresh());
    }

    measurements() {
      const slides = this.slides;
      if (slides.length < 2) return { slides, stride: 0, visible: slides.length, pages: 1, maximum: 0 };
      const first = slides[0];
      const second = slides[1];
      const stride = Math.abs(second.offsetLeft - first.offsetLeft) || first.getBoundingClientRect().width;
      const itemWidth = first.getBoundingClientRect().width || stride;
      const gap = Math.max(0, stride - itemWidth);
      const visible = Math.max(1, Math.floor((this.slider.clientWidth + gap + 1) / stride));
      return {
        slides,
        stride,
        visible: Math.min(slides.length, visible),
        pages: Math.max(1, slides.length - Math.min(slides.length, visible) + 1),
        maximum: Math.max(0, this.slider.scrollWidth - this.slider.clientWidth)
      };
    }

    refresh() {
      const { stride, pages, maximum } = this.measurements();
      const current = stride ? Math.min(pages, Math.max(1, Math.round(this.progress / stride) + 1)) : 1;
      if (this.currentElement) this.currentElement.textContent = String(current);
      if (this.totalElement) this.totalElement.textContent = String(pages);

      const atStart = this.progress <= 1;
      const atEnd = maximum <= 1 || this.progress >= maximum - 1;
      this.previousButton.toggleAttribute('disabled', atStart);
      this.nextButton.toggleAttribute('disabled', atEnd);
      window.ZoriaCarouselFocus?.sync(this.slider);
    }

    onScroll() {
      this.queueRefresh();
    }

    onPrevious(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const { stride } = this.measurements();
      if (stride) this.slider.scrollBy({ left: (this.isRtl ? 1 : -1) * stride, behavior: 'smooth' });
    }

    onNext(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const { stride } = this.measurements();
      if (stride) this.slider.scrollBy({ left: (this.isRtl ? -1 : 1) * stride, behavior: 'smooth' });
    }
  }

  if (!customElements.get('zoria-related-products-slider')) {
    customElements.define('zoria-related-products-slider', ZoriaRelatedProductsSlider);
  }
})();
